const API_URL = process.env.REACT_APP_API_URL || 'https://api.playkokki.com/';
const BASE_URL = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;

// ==================== TYPES ====================

export interface Kokki {
    id: number;
    name: string;
    imageUrl: string | null;
    story: string | null;
    description: string | null;
    characteristics: string | null;
    createdAt: string;
    updatedAt: string;
    emotes: KokkiEmote[];
    powerups: KokkiPowerup[];
    skins: KokkiSkin[];
}

export interface KokkiSkin {
    id: number;
    name: string;
    imageUrl: string | null;
    kokkiId: number;
    createdAt: string;
    updatedAt: string;
}

export interface Avatar {
    id: number;
    name: string;
    imageUrl: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface KokkiEmote {
    id: number;
    name: string;
    imageUrl: string;
    kokkiId: number;
    createdAt: string;
    updatedAt: string;
}

export interface PowerupTemplate {
    id: number;
    name: string;
    description: string | null;
    effectKey: string;
    svgIcon: string | null;
    effectGif: string | null;
    type: 'normal' | 'ultimate';
    gameType: 'flipCardGame' | 'quizMarathon' | 'doubleDown';
    gameDisplayName?: string; // 'Bandido' | 'Zooma' | 'Rolla'
    createdAt: string;
    updatedAt: string;
}

/** Maps internal game type to display name */
export const GAME_DISPLAY_NAMES: Record<string, string> = {
    flipCardGame: 'Bandido',
    quizMarathon: 'Zooma',
    doubleDown: 'Rolla',
};

export function getGameDisplayName(gameType: string): string {
    return GAME_DISPLAY_NAMES[gameType] ?? gameType;
}

export interface KokkiPowerup {
    id: number;
    charges: number;
    kokkiId: number;
    templateId: number;
    template: PowerupTemplate;
    createdAt: string;
    updatedAt: string;
}

export interface CreateKokkiDto {
    name: string;
    imageUrl?: string;
    story?: string;
    description?: string;
    characteristics?: string;
}

export interface UpdateKokkiDto {
    name?: string;
    imageUrl?: string;
    story?: string;
    description?: string;
    characteristics?: string;
}

export interface CreateKokkiEmoteDto {
    name: string;
    imageUrl?: string;
    kokkiId: number;
}

export interface CreatePowerupTemplateDto {
    name: string;
    description?: string;
    effectKey: string;
    svgIcon?: string;
    effectGif?: string;
    type: 'normal' | 'ultimate';
    gameType: 'flipCardGame' | 'quizMarathon' | 'doubleDown';
}

export interface UpdatePowerupTemplateDto {
    name?: string;
    description?: string;
    effectKey?: string;
    svgIcon?: string;
    effectGif?: string;
    type?: 'normal' | 'ultimate';
    gameType?: 'flipCardGame' | 'quizMarathon' | 'doubleDown';
}

export interface CreateKokkiPowerupDto {
    kokkiId: number;
    templateId: number;
    charges?: number;
}

export interface UpdateKokkiPowerupDto {
    charges?: number;
}

export interface CreateKokkiSkinDto {
    name: string;
    imageUrl?: string;
    kokkiId: number;
}

export interface UpdateKokkiSkinDto {
    name?: string;
    imageUrl?: string;
}

export interface CreateAvatarDto {
    name: string;
    imageUrl?: string;
}

export interface UpdateAvatarDto {
    name?: string;
    imageUrl?: string;
}

// ==================== AUTH ====================

const getAuthToken = (): string | null => localStorage.getItem('kokki-jwt-token');

const getAuthHeaders = (): HeadersInit => {
    const token = getAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
};

const getAuthHeadersNoContent = (): HeadersInit => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
};

// ==================== FILE UPLOAD ====================

export const uploadFile = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}kokki/upload`, {
        method: 'POST',
        headers: getAuthHeadersNoContent(),
        body: formData,
    });
    if (!response.ok) throw new Error('Failed to upload file');
    return response.json();
};

// ==================== KOKKI CRUD ====================

export const fetchAllKokkis = async (): Promise<Kokki[]> => {
    const response = await fetch(`${BASE_URL}kokki`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch kokkis');
    return response.json();
};

export const fetchKokkiById = async (id: number): Promise<Kokki> => {
    const response = await fetch(`${BASE_URL}kokki/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch kokki');
    return response.json();
};

export const createKokki = async (data: CreateKokkiDto): Promise<Kokki> => {
    const response = await fetch(`${BASE_URL}kokki`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to create kokki'); }
    return response.json();
};

export const updateKokki = async (id: number, data: UpdateKokkiDto): Promise<Kokki> => {
    const response = await fetch(`${BASE_URL}kokki/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to update kokki'); }
    return response.json();
};

export const deleteKokki = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete kokki'); }
};

// ==================== EMOTE CRUD ====================

export const createEmote = async (data: CreateKokkiEmoteDto): Promise<KokkiEmote> => {
    const response = await fetch(`${BASE_URL}kokki/emote`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to create emote'); }
    return response.json();
};

export const deleteEmote = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/emote/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete emote'); }
};

// ==================== POWERUP TEMPLATE BANK CRUD ====================

export const fetchAllTemplates = async (): Promise<PowerupTemplate[]> => {
    const response = await fetch(`${BASE_URL}kokki/template/all`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch templates');
    return response.json();
};

export const fetchTemplatesByGame = async (gameType: string): Promise<PowerupTemplate[]> => {
    const response = await fetch(`${BASE_URL}kokki/template/game/${gameType}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch templates');
    return response.json();
};

export const createTemplate = async (data: CreatePowerupTemplateDto): Promise<PowerupTemplate> => {
    const response = await fetch(`${BASE_URL}kokki/template`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to create template'); }
    return response.json();
};

export const updateTemplate = async (id: number, data: UpdatePowerupTemplateDto): Promise<PowerupTemplate> => {
    const response = await fetch(`${BASE_URL}kokki/template/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to update template'); }
    return response.json();
};

export const deleteTemplate = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/template/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete template'); }
};

// ==================== KOKKI ↔ POWERUP ASSIGNMENT ====================

export const assignPowerup = async (data: CreateKokkiPowerupDto): Promise<KokkiPowerup> => {
    const response = await fetch(`${BASE_URL}kokki/powerup`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to assign powerup'); }
    return response.json();
};

export const updatePowerup = async (id: number, data: UpdateKokkiPowerupDto): Promise<KokkiPowerup> => {
    const response = await fetch(`${BASE_URL}kokki/powerup/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to update powerup'); }
    return response.json();
};

export const deletePowerup = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/powerup/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete powerup'); }
};

// ==================== SKIN CRUD ====================

export const createSkin = async (data: CreateKokkiSkinDto): Promise<KokkiSkin> => {
    const response = await fetch(`${BASE_URL}kokki/skin`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to create skin'); }
    return response.json();
};

export const updateSkin = async (id: number, data: UpdateKokkiSkinDto): Promise<KokkiSkin> => {
    const response = await fetch(`${BASE_URL}kokki/skin/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to update skin'); }
    return response.json();
};

export const deleteSkin = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/skin/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete skin'); }
};

// ==================== AVATAR (USER PROFILE) CRUD ====================

export const fetchAllAvatars = async (): Promise<Avatar[]> => {
    const response = await fetch(`${BASE_URL}kokki/avatar/all`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch avatars');
    return response.json();
};

export const fetchAvatarById = async (id: number): Promise<Avatar> => {
    const response = await fetch(`${BASE_URL}kokki/avatar/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch avatar');
    return response.json();
};

export const createAvatar = async (data: CreateAvatarDto): Promise<Avatar> => {
    const response = await fetch(`${BASE_URL}kokki/avatar`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to create avatar'); }
    return response.json();
};

export const updateAvatar = async (id: number, data: UpdateAvatarDto): Promise<Avatar> => {
    const response = await fetch(`${BASE_URL}kokki/avatar/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to update avatar'); }
    return response.json();
};

export const deleteAvatar = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}kokki/avatar/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || 'Failed to delete avatar'); }
};
